/* eslint-disable react/jsx-no-bind */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Spin, Empty, Checkbox, Form, message, Icon as AntIcon } from 'antd';
import classNames from 'classnames';
import Button from '../../../../components/Button/Button';
import Icon from '../../../../components/Icon/Icon';
import { renderColor } from '../../../Industry/utils';
import TextEditor from '../../../../components/TextEditor/TextEditor';

import { fetchRiskChecklist } from '../../../Industry/IndustryActions';
import { underwriterHazardIndexCustomizationSelector } from '../../ReportSelectors';

import './RiskCheckListEdit.scss';

/** Align API + Redux checklist ids — JSON mixes string/number `questionId` vs `risk.id`. */
function normalizeRiskQuestionId(raw) {
  if (raw == null || raw === '') return raw;
  const n = Number(raw);
  return Number.isNaN(n) ? raw : n;
}

function isRiskQuestionSelected(checkedIds, questionId) {
  const q = normalizeRiskQuestionId(questionId);
  return checkedIds.some((id) => normalizeRiskQuestionId(id) === q);
}

function normalizedIdSetsEqual(a, b) {
  if (a.length !== b.length) return false;
  const sort = (xs) =>
    [...xs].map(normalizeRiskQuestionId).slice().sort((x, y) => String(x).localeCompare(String(y)));
  const sa = sort(a);
  const sb = sort(b);
  return sa.every((v, i) => v === sb[i]);
}

/** Row id fields from persisted JSON may use camelCase or snake_case */
function savedChecklistQuestionId(row) {
  const raw = row?.questionId ?? row?.question_id;
  return normalizeRiskQuestionId(raw);
}

/** Stable signature for saved checklist rows (question ids only) — drives hydration when Redux updates. */
function serializeSavedChecklistIds(rows) {
  if (!rows?.length) return '';
  return rows
    .map((r) => savedChecklistQuestionId(r))
    .sort((a, b) => String(a).localeCompare(String(b)))
    .join(',');
}

const RiskCheckListEdit = ({
  riskChecklist,
  fetchRiskChecklist,
  loadingRiskChecklist,
  hazardIndex,
  customization,
  loadingCustomization,
  form,
  onUnderwriterComments,
  connectChecklistForm,
  syncChecklistState,
  onMergedUnderwriterSave,
}) => {
  const { id: industryId } = useParams();
  const [checklist, setChecklist] = useState([]);
  const [checkedList, setCheckedList] = useState([]);
  const [checkedAnswerList, setCheckedAnswerList] = useState([]);
  /** After user changes checkboxes, don't overwrite with Redux until server checklist signature changes or modal remounts. */
  const checklistDirtyRef = useRef(false);
  /** Last applied server checklist id-signature (avoids duplicate setState; pairs with dirty ref). */
  const lastHydratedServerSigRef = useRef(null);

  const { getFieldDecorator } = form;

  useLayoutEffect(() => {
    if (connectChecklistForm) {
      connectChecklistForm(form);
      return () => connectChecklistForm(null);
    }
    return undefined;
  }, [connectChecklistForm, form]);

  useEffect(() => {
    checklistDirtyRef.current = false;
    lastHydratedServerSigRef.current = null;
  }, [hazardIndex?.id]);

  useEffect(() => {
    if (syncChecklistState) syncChecklistState(checkedList);
  }, [checkedList, syncChecklistState]);

  const savedChecklistForClass = useMemo(() => {
    const cls = customization?.customizations?.find(
      (hazardClass) => Number(hazardClass?.id) === Number(hazardIndex?.id)
    );
    const raw = cls?.checklist;
    return Array.isArray(raw) ? raw : [];
  }, [customization?.customizations, hazardIndex?.id]);

  /** Saved answers for this class (rename kept for JSX below). */
  const answers = savedChecklistForClass;

  /** Persists only via UnderwriterEdit merged Save — never dispatches API from checkbox/text edits here. */
  function handleSaveChecklist() {
    if (!onMergedUnderwriterSave) {
      message.warn('Save is not available.');
      return;
    }
    onMergedUnderwriterSave();
  }

  useEffect(() => {
    if (!industryId) return;
    const insuranceClasses = riskChecklist?.map((cl) => cl?.insurance_class?.id);
    const noOfInsuranceClasses = [...new Set(insuranceClasses)].length;
    if (noOfInsuranceClasses < 2 && !loadingRiskChecklist) {
      fetchRiskChecklist(industryId, { type: 'Key' });
    }
    // Intentionally run once per mount — same as previous behavior; hydration uses savedChecklistForClass below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industryId]);

  useEffect(() => {
    const rows = savedChecklistForClass;
    const serverSig = serializeSavedChecklistIds(rows);
    if (serverSig === lastHydratedServerSigRef.current) return;
    if (checklistDirtyRef.current) return;

    lastHydratedServerSigRef.current = serverSig;

    const nextCheckedIds = rows.map((row) => savedChecklistQuestionId(row));
    const nextAnswerExpandedIds = rows
      .filter((row) => row.answer || row.answer_html)
      .map((row) => savedChecklistQuestionId(row));

    setCheckedList((prev) => (normalizedIdSetsEqual(prev, nextCheckedIds) ? prev : nextCheckedIds));
    setCheckedAnswerList((prev) =>
      normalizedIdSetsEqual(prev, nextAnswerExpandedIds) ? prev : nextAnswerExpandedIds
    );
  }, [savedChecklistForClass]);

  useEffect(() => {
    const checklistByIndex = riskChecklist.filter(
      (list) => Number(list?.insurance_class?.id) === Number(hazardIndex?.id)
    );
    setChecklist(checklistByIndex);
  }, [riskChecklist, hazardIndex?.id]);

  const onRiskItemCheckboxChange = useCallback(
    (itemId, nextChecked, e) => {
      e?.stopPropagation?.();
      checklistDirtyRef.current = true;
      const nid = normalizeRiskQuestionId(itemId);
      const nextList = nextChecked
        ? isRiskQuestionSelected(checkedList, nid)
          ? checkedList
          : [...checkedList, nid]
        : checkedList.filter((x) => normalizeRiskQuestionId(x) !== nid);
      setCheckedList(nextList);
    },
    [checkedList]
  );

  const onSelectAllChange = useCallback(
    (e) => {
      e.stopPropagation();
      checklistDirtyRef.current = true;
      const nextList = e.target.checked ? checklist.map((q) => normalizeRiskQuestionId(q.id)) : [];
      setCheckedList(nextList);
    },
    [checklist]
  );

  const onCheckAnswer = (id) => {
    const nid = normalizeRiskQuestionId(id);
    if (checkedAnswerList.some((item) => normalizeRiskQuestionId(item) === nid)) {
      const answerList = checkedAnswerList.filter((item) => normalizeRiskQuestionId(item) !== nid);
      setCheckedAnswerList(answerList);
    } else {
      const answerList = [...checkedAnswerList, nid];
      setCheckedAnswerList(answerList);
    }
  };

  const selectedQuestionCount = checklist.filter((q) => isRiskQuestionSelected(checkedList, q.id)).length;
  const selectAllChecked = checklist.length > 0 && selectedQuestionCount === checklist.length;
  const selectAllIndeterminate = selectedQuestionCount > 0 && selectedQuestionCount < checklist.length;

  return (
    <Form name="riskChecklistForm" onSubmit={(e) => e.preventDefault()}>
      <div className="risk-checklist-container">
        <div className="heading">
          <div className="hazard-index-icon">
            <Icon
              size={40}
              component={hazardIndex.icon}
              theme="filled"
              style={{ backgroundColor: renderColor(hazardIndex.rating) }}
            />
          </div>
          <div className="info">
            <div className="title">
              <h2 className="name">{hazardIndex.name}</h2>
              <div className="rating" style={{ color: renderColor(hazardIndex.rating) }}>
                {`${hazardIndex.rating}%`}
              </div>
            </div>
            <div className="classes">{hazardIndex.classes.join(', ')}</div>
          </div>
        </div>
        <div className="content">
          {loadingRiskChecklist ? (
            <Spin />
          ) : (
            <>
              <h3 className="risk-checklist-heading">
                <Checkbox
                  className="check-box risk-checklist-select-all"
                  checked={selectAllChecked}
                  indeterminate={selectAllIndeterminate}
                  disabled={checklist.length === 0 || loadingRiskChecklist}
                  onChange={onSelectAllChange}
                  aria-label={
                    selectAllChecked ? 'Deselect all risk checklist questions' : 'Select all risk checklist questions'
                  }
                />
                <span className="risk-checklist-heading-title">Risk Checklist</span>
              </h3>
              {checklist.length > 0 &&
                checklist.map((list, idx) => {
                  const answerRow = answers.find(
                    (a) => savedChecklistQuestionId(a) === normalizeRiskQuestionId(list.id)
                  );
                  const answer = answerRow?.answer ?? answerRow?.answer_html;
                  const qid = normalizeRiskQuestionId(list.id);
                  const isAnswerExpanded = checkedAnswerList.some((x) => normalizeRiskQuestionId(x) === qid);

                  return (
                    <div
                      className={classNames('check-list-item', {
                        selected: isRiskQuestionSelected(checkedList, list.id),
                      })}
                      key={list.id}
                    >
                      <Checkbox
                        className="check-box"
                        checked={isRiskQuestionSelected(checkedList, list.id)}
                        onChange={(e) => onRiskItemCheckboxChange(qid, e.target.checked, e)}
                      />
                      <span className="check-list-item-label">{`${idx + 1}. ${list.title}`}</span>

                      {isRiskQuestionSelected(checkedList, list.id) && (
                        <div className="check-list-item-answer-section">
                          <div className="check-list-item-answer-toolbar">
                            <button
                              type="button"
                              className={classNames('risk-checklist-toggle-answer', {
                                'is-expanded': isAnswerExpanded,
                              })}
                              onClick={() => onCheckAnswer(qid)}
                              aria-expanded={isAnswerExpanded}
                            >
                              <AntIcon type={isAnswerExpanded ? 'up' : 'down'} aria-hidden />
                              <span>{isAnswerExpanded ? 'Hide answer' : 'Add answer'}</span>
                            </button>
                          </div>
                          {isAnswerExpanded && (
                            <div className="check-list-item-answer-editor">
                              <Form.Item className="check-list-item-answer-field">
                                {getFieldDecorator(String(qid), {
                                  initialValue: answer,
                                })(<TextEditor placeholder="Enter your answer for this checklist item." />)}
                              </Form.Item>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              {!loadingRiskChecklist && checklist.length === 0 && (
                <Empty description="No checklist items for this class." />
              )}
            </>
          )}
        </div>

        <div className="risk-checklist-toolbar" role="toolbar" aria-label="Checklist navigation and save">
          <Button
            secondary
            htmlType="button"
            onClick={onUnderwriterComments}
            className={classNames('risk-checklist-back-button', 'underwriter-modal-nav-button')}
          >
            <AntIcon type="left" aria-hidden />
            Back to comments
          </Button>
          {!loadingRiskChecklist && checklist.length > 0 ? (
            <Button
              secondary
              type="primary"
              htmlType="button"
              className="risk-checklist-save-button"
              onClick={handleSaveChecklist}
              disabled={loadingCustomization}
            >
              Save
            </Button>
          ) : null}
        </div>
      </div>
    </Form>
  );
};

const mapStateToProps = (state) => ({
  riskChecklist: state.industry.riskChecklist || [],
  loadingRiskChecklist: state.industry.loadingRiskChecklist,
  customization: underwriterHazardIndexCustomizationSelector(state),
  loadingCustomization: state.report.loadingCustomizations,
});

const mapDispatchToProps = { fetchRiskChecklist };

export default connect(mapStateToProps, mapDispatchToProps)(Form.create()(RiskCheckListEdit));
